import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, Eye, HelpCircle } from 'lucide-react'
import { usePublishedFAQs, useFAQCategories, useIncrementFAQView, useFAQFeedback } from '../hooks/useFAQ'
import { useTranslation } from '../i18n'
import { motion } from 'framer-motion'
import { showSuccessToast, showErrorToast } from '../utils/toast'

export default function FAQPage() {
  const { tObj, language } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '')
  const [expandedFAQs, setExpandedFAQs] = useState([])
  const [feedbackSubmitted, setFeedbackSubmitted] = useState([])

  const { faqs, loading, error, refetch } = usePublishedFAQs({
    category: selectedCategory || undefined,
    language
  })

  const { categories, loading: categoriesLoading } = useFAQCategories({ language })
  const { incrementView } = useIncrementFAQView()
  const { submitFeedback } = useFAQFeedback()

  // Filtrer les FAQs par terme de recherche
  const filteredFAQs = useMemo(() => {
    if (!searchTerm) return faqs

    const term = searchTerm.toLowerCase()
    return faqs.filter(faq => {
      const question = (faq.translation?.question || faq.question || '').toLowerCase()
      const answer = (faq.translation?.answer || faq.answer || '').toLowerCase()
      return question.includes(term) || answer.includes(term)
    })
  }, [faqs, searchTerm])

  // Grouper les FAQs par catégorie
  const faqsByCategory = useMemo(() => {
    const grouped = {}
    filteredFAQs.forEach(faq => {
      const category = faq.category || 'Général'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(faq)
    })
    return grouped
  }, [filteredFAQs])

  // Trier les catégories par ordre
  const sortedCategories = useMemo(() => {
    return Object.keys(faqsByCategory).sort((a, b) => {
      const faqsA = faqsByCategory[a]
      const faqsB = faqsByCategory[b]
      const orderA = faqsA[0]?.order || 0
      const orderB = faqsB[0]?.order || 0
      return orderA - orderB
    })
  }, [faqsByCategory])

  const handleSearch = (value) => {
    setSearchTerm(value)
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set('search', value)
    } else {
      newParams.delete('search')
    }
    setSearchParams(newParams)
  }

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
    const newParams = new URLSearchParams(searchParams)
    if (category) {
      newParams.set('category', category)
    } else {
      newParams.delete('category')
    }
    setSearchParams(newParams)
  }

  const toggleFAQ = async (faqId) => {
    setExpandedFAQs(prev => {
      const isExpanded = prev.includes(faqId)
      if (isExpanded) {
        // Fermer la FAQ
        return prev.filter(id => id !== faqId)
      } else {
        // Ouvrir la FAQ
        // Incrémenter les vues quand on ouvre une FAQ (ne pas bloquer l'affichage)
        incrementView(faqId).catch(err => {
          console.error('Erreur lors de l\'incrémentation des vues:', err)
        })
        return [...prev, faqId]
      }
    })
  }

  const handleFeedback = async (faqId, isHelpful) => {
    if (feedbackSubmitted.includes(faqId)) {
      showErrorToast({ message: 'Vous avez déjà soumis un feedback pour cette FAQ' })
      return
    }

    const result = await submitFeedback(faqId, isHelpful)
    if (result.success) {
      setFeedbackSubmitted(prev => [...prev, faqId])
      showSuccessToast({ message: isHelpful ? 'Merci pour votre feedback positif !' : 'Merci pour votre feedback !' })
      refetch()
    } else {
      showErrorToast({ message: result.error || 'Erreur lors de la soumission du feedback' })
    }
  }

  const reveal = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-15% 0px' },
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header amélioré avec effets visuels - Style Courses */}
      <div className="relative pt-20 pb-12 sm:pt-24 sm:pb-16 lg:pt-28 lg:pb-20 overflow-hidden">
        {/* Effets de fond animés */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#203a8a]/10 via-blue-50/50 to-purple-50/30 pointer-events-none"></div>
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl animate-float delay-500"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent animate-gradient">
              {tObj.faq?.title?.() || 'Questions Fréquemment Posées'}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {tObj.faq?.subtitle?.() || 'Trouvez rapidement les réponses à vos questions'}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters - Amélioré */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative">
        {/* Effets de fond */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-indigo-200 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-200 rounded-full filter blur-3xl"></div>
        </div>
        
        <motion.div {...reveal} className="mb-8 relative z-10" style={{ pointerEvents: 'auto' }}>
          <div className="glass-effect-strong rounded-xl shadow-2xl p-4 sm:p-6 border border-white/30">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={tObj.faq?.searchPlaceholder?.() || 'Rechercher dans les FAQs...'}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] bg-white transition-all duration-300"
                />
              </div>

              {/* Category Filter */}
              <div className="md:w-64">
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] bg-white transition-all duration-300"
                  disabled={categoriesLoading}
                >
                  <option value="">{tObj.faq?.allCategories?.() || 'Toutes les catégories'}</option>
                  {categoriesLoading ? (
                    <option disabled>Chargement...</option>
                  ) : (
                    categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto"></div>
            <p className="mt-4 text-gray-600">{tObj.faq?.loading?.() || 'Chargement des FAQs...'}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <motion.div {...reveal} className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">
                  {tObj.faq?.errorTitle?.() || 'Erreur de chargement'}
                </h3>
                <p className="text-sm text-red-800">
                  {typeof error === 'string' 
                    ? error 
                    : (error?.message || String(error) || 'Impossible de charger les FAQs. Veuillez réessayer plus tard.')}
                </p>
                {(typeof error === 'string' && (error.includes('Network error') || error.includes('ERR_CONNECTION_REFUSED'))) || 
                  (error?.message && (error.message.includes('Network error') || error.message.includes('ERR_CONNECTION_REFUSED'))) ? (
                  <p className="text-xs text-red-700 mt-2">
                    Le serveur n'est pas accessible. Vérifiez votre connexion ou contactez le support.
                  </p>
                ) : null}
              </div>
            </div>
          </motion.div>
        )}

        {/* FAQs List */}
        {!loading && !error && (
          <>
            {!faqs || faqs.length === 0 ? (
              <motion.div {...reveal} className="text-center py-12">
                <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {tObj.faq?.noResults?.() || 'Aucune FAQ trouvée'}
                </h3>
                <p className="text-gray-600">
                  {searchTerm || selectedCategory
                    ? (tObj.faq?.noResultsDesc?.() || 'Essayez de modifier vos critères de recherche')
                    : 'Aucune FAQ disponible pour le moment'}
                </p>
              </motion.div>
            ) : filteredFAQs.length === 0 ? (
              <motion.div {...reveal} className="text-center py-12">
                <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {tObj.faq?.noResults?.() || 'Aucune FAQ trouvée'}
                </h3>
                <p className="text-gray-600">
                  {tObj.faq?.noResultsDesc?.() || 'Essayez de modifier vos critères de recherche'}
                </p>
              </motion.div>
            ) : (
              <div className="space-y-8">
                {sortedCategories.map((category, categoryIndex) => (
                  <motion.div
                    key={category}
                    {...reveal}
                    transition={{ ...reveal.transition, delay: categoryIndex * 0.1 }}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <div className="mb-8">
                      <h2 className="text-3xl font-bold bg-gradient-to-r from-[#141835] to-[#203a8a] bg-clip-text text-transparent mb-3">
                        {category}
                      </h2>
                      <div className="h-1.5 w-24 bg-gradient-to-r from-[#203a8a] to-purple-600 rounded-full"></div>
                    </div>

                    <div className="space-y-4">
                      {faqsByCategory[category].map((faq) => {
                        const isExpanded = expandedFAQs.includes(faq.id)
                        const question = faq.translation?.question || faq.question
                        const answer = faq.translation?.answer || faq.answer
                        const hasFeedback = feedbackSubmitted.includes(faq.id)

                        return (
                          <div
                            key={faq.id}
                            className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl hover:border-[#203a8a] transition-all duration-300 hover-lift group relative z-10"
                          >
                            <button
                              onClick={() => toggleFAQ(faq.id)}
                              className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 group relative z-20"
                              style={{ pointerEvents: 'auto' }}
                            >
                              <div className="flex-1 pr-4">
                                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 group-hover:text-[#203a8a] transition-colors">
                                  {question}
                                </h3>
                                <div className="flex items-center gap-4 text-xs sm:text-sm text-gray-500">
                                  {faq.helpfulCount > 0 && (
                                    <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                                      <ThumbsUp className="w-4 h-4" />
                                      {faq.helpfulCount}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                                isExpanded 
                                  ? 'bg-gradient-to-br from-[#203a8a] to-[#2563eb] text-white rotate-180' 
                                  : 'bg-gray-100 text-gray-400 group-hover:bg-[#203a8a] group-hover:text-white'
                              }`}>
                                {isExpanded ? (
                                  <ChevronUp className="w-5 h-5 flex-shrink-0" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 flex-shrink-0" />
                                )}
                              </div>
                            </button>

                            {isExpanded && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="px-4 sm:px-6 pb-4 sm:pb-5 border-t-2 border-gray-200 bg-gradient-to-br from-gray-50/50 to-white overflow-hidden"
                                style={{ display: 'block' }}
                              >
                                <div className="pt-4">
                                  {answer ? (
                                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line mb-4">
                                      {answer}
                                    </p>
                                  ) : (
                                    <p className="text-sm sm:text-base text-gray-500 italic mb-4">
                                      Aucune réponse disponible pour le moment.
                                    </p>
                                  )}

                                  {/* Feedback Buttons - Amélioré */}
                                  {!hasFeedback && (
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-4 border-t border-gray-200">
                                      <span className="text-xs sm:text-sm font-medium text-gray-700">
                                        {tObj.faq?.wasHelpful?.() || 'Cette réponse vous a-t-elle été utile ?'}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => handleFeedback(faq.id, true)}
                                          className="flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-xl transition-all duration-300 hover:scale-105 border border-green-200"
                                        >
                                          <ThumbsUp className="w-4 h-4" />
                                          {tObj.faq?.yes?.() || 'Oui'}
                                        </button>
                                        <button
                                          onClick={() => handleFeedback(faq.id, false)}
                                          className="flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-xl transition-all duration-300 hover:scale-105 border border-red-200"
                                        >
                                          <ThumbsDown className="w-4 h-4" />
                                          {tObj.faq?.no?.() || 'Non'}
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {hasFeedback && (
                                    <div className="pt-4 border-t border-gray-200">
                                      <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-xl">
                                        <ThumbsUp className="w-5 h-5 text-green-600" />
                                        <p className="text-xs sm:text-sm font-medium text-green-700">
                                          {tObj.faq?.feedbackSubmitted?.() || 'Merci pour votre feedback !'}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
